import React, { useState, useEffect, useContext } from "react"
import BackToButton from "../../BackToButton"
import CheckAvailability from "./step-views/CheckAvailability"
import ConfirmSend from "./step-views/ConfirmSend"
import InviteContacts from "./step-views/InviteContacts"
import SelectService from "./step-views/SelectService"
import { AvailabilityRulesData } from "../../../types/availabilityTypes"
import { ContactRequests, CalendarRequests } from "../../../api/app.service"
import {
  Meeting,
  MeetingPrefillOptions,
  MeetingType,
} from "../../../types/meetingTypes"
import { UserCtx } from "../../../context/userContext"
import { useToast } from "../../../hooks/useToast"

type Props = {
  // mandatory props for edit and new
  newMeetingType: MeetingType
  setNewMeetingType: any
  // used for pre-populating new meeting on links from other pages e.g. Schedule Session from ServicePreviewElement.tsx
  prefillOptions?: MeetingPrefillOptions | null
  // include following 2 props when using this component as new meeting interface from Scheduling.tsx
  setShowNewMeeting?: any
  setShowCalendar?: any
  // include following 4 props when using this component as edit meeting interface from ZoeeSessionDetailView.tsx
  existingMeetingData?: Meeting
  initialStep?: string
  isEditing?: boolean
  setActiveSessionDetailViewId?: any
  showRescheduleMeeting: boolean
  setShowRescheduleMeeting: any
  clickReschedule: boolean
  setClickReschedule: any
  clickTodayReschedule: boolean
  backToContacts: boolean
  contact: any
  keepBackToButton: boolean
  meeting?: any
  otherGuests?: any
}

const NewMeeting: React.FC<Props> = ({
  newMeetingType,
  setNewMeetingType,
  setShowNewMeeting,
  setShowCalendar,
  existingMeetingData,
  initialStep,
  isEditing,
  setActiveSessionDetailViewId,
  prefillOptions,
  showRescheduleMeeting,
  setShowRescheduleMeeting,
  clickReschedule,
  clickTodayReschedule,
  backToContacts,
  contact,
  keepBackToButton,
  meeting,
  otherGuests,
}) => {
  const [step, setStep] = useState<number>(1)
  const { user } = useContext(UserCtx)
  const userActiveProfile = user.activeProfile
  const { toast } = useToast()
  const [userContacts, setUserContacts] = useState<any>([])
  const [userAvailabilities, setUserAvailabilities] = useState<
    Array<AvailabilityRulesData>
  >([])
  const [loadingNewMeeting, setLoadingNewMeeting] = useState<boolean>(false)
  const [coachDetails, setCoachDetails] = useState<any>({})
  const [newMeetingData, setNewMeetingData] = useState<Meeting>({
    id: null,
    meetingType: newMeetingType,
    coach_user_id: "",
    type: null,
    inProgress: false,
    contacts: [],
    meetingName: null,
    status: "busy",
    medium: "zoee_video",
    date: null,
    startTime: null,
    meetingEnded: null,
    endTime: null,
    duration: null,
    service_usage_id: null,
    timezone: user.timezone || null,
    service_details: null,
    notifications: [],
    calendar: "zoee",
    stripe_product_id: null,
  })

  const getContacts = () => {
    if (user?.coachProfile) {
      setLoadingNewMeeting(true)
      ContactRequests.getContactList({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
        .then((data) => {
          setUserContacts(data.contact_list)
          setLoadingNewMeeting(false)
        })
        .catch((err) => {
          console.error(err)
          toast.error(err.response.data.message)
          setLoadingNewMeeting(false)
        })
    }
  }

  const getAvailabilityRules = () => {
    let coachUserId: string = ""
    if (prefillOptions) {
      let coachdata
      coachUserId = prefillOptions?.service?.coach_user_id
      if (parseInt(user.user_id) === parseInt(coachUserId)) {
        coachdata = user.activeProfileId
      } else {
        coachdata = prefillOptions?.contact?.profile_id
      }
      setCoachDetails({ coachUserId: coachUserId, coachProfileId: coachdata })
    } else if (existingMeetingData) {
      coachUserId = existingMeetingData?.coach_user_id

      let coachdata = existingMeetingData?.contacts?.find(
        (user) => user.user_id === coachUserId
      )
      setCoachDetails({
        coachUserId: coachUserId,
        coachProfileId: coachdata?.profile_id,
      })
    } else if (newMeetingData) {
      coachUserId =
        newMeetingData?.coach_user_id === ""
          ? user.user_id
          : newMeetingData?.coach_user_id
      let coachdata =
        coachUserId === user.user_id
          ? user.activeProfileId
          : newMeetingData?.contacts?.find((c) => c.user_id === coachUserId)
              ?.profile_id
      setCoachDetails({ coachUserId: coachUserId, coachProfileId: coachdata })
    }
    if (coachUserId) {
      setLoadingNewMeeting(true)
      CalendarRequests.getAvailabilityRules({
        user_id: parseInt(coachUserId),
      })
        .then((data) => {
          setUserAvailabilities(data.availability_rules)
          // TODO add logic for checking for default schedule
          setLoadingNewMeeting(false)
        })
        .catch((err) => {
          console.error(err)
          setLoadingNewMeeting(false)
        })
    }
  }

  const handleEditClick = (stepName: string) => {
    if (newMeetingType === "individual") {
      switch (stepName) {
        case "attendees":
          setStep(1)
          break
        case "services":
          setStep(2)
          break
        case "availability":
          setStep(3)
          break
        default:
          break
      }
    } else if (newMeetingType === "group") {
      switch (stepName) {
        case "services":
          setStep(1)
          break
        case "attendees":
          setStep(2)
          break
        case "availability":
          setStep(3)
          break
        default:
          break
      }
    } else {
      switch (stepName) {
        case "attendees":
          setStep(1)
          break
        case "availability":
          setStep(2)
          break
        default:
          break
      }
    }
  }

  useEffect(() => {
    if (!prefillOptions?.contact) return

    setNewMeetingData((prev) => {
      const alreadyHasContact = prev.contacts.some(
        (c: any) => c.profile_id === prefillOptions.contact.profile_id
      )

      // If both contact + service are present
      if (prefillOptions.service && !alreadyHasContact) {
        setNewMeetingType("individual")
        setStep(3)
        return {
          ...prev,
          contacts: [...prev.contacts, prefillOptions.contact],
          duration:
            prefillOptions.service.service_details.session.duration / 60,
          service_details: prefillOptions.service.service_details,
          service_usage_id: prefillOptions.service.service_usage_id,
          stripe_product_id: prefillOptions.service.stripe_product_id,
          coach_user_id: prefillOptions.service.coach_user_id,
        }
      }

      // If only contact is present (no service yet)
      if (!prefillOptions.service && !alreadyHasContact) {
        setNewMeetingType("individual")
        setStep(2)
        return {
          ...prev,
          contacts: [...prev.contacts, prefillOptions.contact],
        }
      }

      // If contact already exists, don’t re-add
      return prev
    })
  }, [
    prefillOptions?.contact?.profile_id,
    prefillOptions?.service?.service_usage_id,
  ])

  useEffect(() => {
    getContacts()
    getAvailabilityRules()
  }, [user])

  useEffect(() => {
    getAvailabilityRules()
  }, [newMeetingData])

  useEffect(() => {
    if (existingMeetingData) {
      setNewMeetingData(existingMeetingData)
      setNewMeetingType(existingMeetingData.meetingType)
    }

    if (initialStep) {
      handleEditClick(initialStep)
    }
  }, [existingMeetingData, initialStep])

  const marginTop = clickTodayReschedule
    ? "-mt-0 md:-mt-[55px]"
    : clickReschedule
    ? "-mt-0 md:-mt-[12px]"
    : "-mt-0 md:-mt-[67px]"

  return (
    <>
      <div className="mt-[12px] flex w-full flex-col items-center">
        <div className={`w-full max-w-[1128px] md:px-[16px] ${marginTop}`}>
          <div>
            {!showRescheduleMeeting ? (
              !existingMeetingData ? (
                <div
                  onClick={() => {
                    setShowNewMeeting(false)
                    setShowCalendar(true)
                  }}
                  className="mb-[24px] w-screen px-[12px] md:w-auto md:px-0"
                >
                  {keepBackToButton ? (
                    <BackToButton
                      to={`/${userActiveProfile}/scheduling`}
                      current="New Meeting"
                      prev="Calendar"
                    />
                  ) : (
                    <div></div>
                  )}
                </div>
              ) : (
                <div
                  onClick={() => {
                    setShowNewMeeting(false)
                    setActiveSessionDetailViewId(existingMeetingData.id)
                  }}
                  className="mb-[24px] w-screen px-[12px] md:w-auto md:px-0"
                >
                  {backToContacts ? (
                    <BackToButton
                      to={`/${userActiveProfile}/contacts/${contact.profile_id}`}
                      current="Reschedule Meeting"
                      prev="Meeting Details"
                    />
                  ) : (
                    <BackToButton
                      to={`/${userActiveProfile}/scheduling`}
                      current="Reschedule Meeting"
                      prev="Meeting Details"
                    />
                  )}
                </div>
              )
            ) : (
              <div
                onClick={() => {
                  setShowRescheduleMeeting(false)
                  setActiveSessionDetailViewId(null)
                }}
                className="mb-[24px] w-screen px-[12px] md:w-auto md:px-0"
              >
                {backToContacts ? (
                  <BackToButton
                    to={`/${userActiveProfile}/contacts/${contact.profile_id}`}
                    current="Reschedule Meeting"
                    prev="Contact Profile"
                  />
                ) : (
                  <BackToButton
                    to={`/${userActiveProfile}/scheduling`}
                    current="Reschedule Meeting"
                    prev="Calendar"
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex w-full justify-center md:max-w-[1128px] md:gap-[64px] md:px-[16px]">
            <div className="w-[360px] md:w-[598px] lg:w-[798px] mx-auto">
              {newMeetingType === "individual" && (
                <>
                  {step === 1 && (
                    <InviteContacts
                      userContacts={userContacts}
                      setStep={setStep}
                      newMeetingType={newMeetingType}
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      isLoading={loadingNewMeeting}
                      coachDetails={coachDetails}
                    />
                  )}
                  {step === 2 && (
                    <SelectService
                      setNewMeetingType={setNewMeetingType}
                      newMeetingData={newMeetingData}
                      isEditing={isEditing}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      setShowNewMeeting={setShowNewMeeting}
                      setActiveSessionDetailViewId={
                        setActiveSessionDetailViewId
                      }
                      existingMeetingData={existingMeetingData}
                    />
                  )}
                  {step === 3 && (
                    <CheckAvailability
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      availabilities={userAvailabilities}
                      coachDetails={coachDetails}
                      meeting={meeting}
                    />
                  )}
                  {step === 4 && (
                    <ConfirmSend
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      newMeetingType={newMeetingType}
                      isEditing={isEditing}
                      setShowCalendar={setShowCalendar}
                      setShowNewMeeting={setShowNewMeeting}
                      otherGuests={otherGuests}
                    />
                  )}
                </>
              )}
              {newMeetingType === "group" && (
                <>
                  {step === 1 && (
                    <SelectService
                      setNewMeetingType={setNewMeetingType}
                      newMeetingData={newMeetingData}
                      isEditing={isEditing}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                    />
                  )}
                  {step === 2 && (
                    <InviteContacts
                      userContacts={userContacts}
                      setStep={setStep}
                      newMeetingType={newMeetingType}
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      isLoading={loadingNewMeeting}
                      coachDetails={coachDetails}
                    />
                  )}
                  {step === 3 && (
                    <CheckAvailability
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      availabilities={userAvailabilities}
                      coachDetails={coachDetails}
                      meeting={meeting}
                    />
                  )}
                  {step === 4 && (
                    <ConfirmSend
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      newMeetingType={newMeetingType}
                      isEditing={isEditing}
                      setShowCalendar={setShowCalendar}
                      setShowNewMeeting={setShowNewMeeting}
                    />
                  )}
                </>
              )}
              {newMeetingType === "custom" && (
                <>
                  {step === 1 && (
                    <InviteContacts
                      userContacts={userContacts}
                      setStep={setStep}
                      newMeetingType={newMeetingType}
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      isLoading={loadingNewMeeting}
                      coachDetails={coachDetails}
                    />
                  )}
                  {step === 2 && (
                    <CheckAvailability
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      availabilities={userAvailabilities}
                      coachDetails={coachDetails}
                      meeting={meeting}
                    />
                  )}
                  {step === 3 && (
                    <ConfirmSend
                      newMeetingData={newMeetingData}
                      setNewMeetingData={setNewMeetingData}
                      setStep={setStep}
                      newMeetingType={newMeetingType}
                      isEditing={isEditing}
                      setShowCalendar={setShowCalendar}
                      setShowNewMeeting={setShowNewMeeting}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NewMeeting
